import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Button, Row, Col, ProgressBar } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

import history from '../../../core/history';
import ListCoverPhoto from '../../ListCoverPhoto';
import PublishOption from './PublishOption';

import { removeListing } from '../../../actions/removeListing';
import messages from '../../../locale/messages';

import closeIcon from '/public/SiteIcons/listCloseIcon.svg';
import s from './ListItem.css';
import bt from '../../../components/commonStyle.css';

const ListItem = ({ data, refetch, searchKey, listId: propListId, ManagePublishStatus, ...props }) => {
  const [isRemove, setIsRemove] = useState(false);
  const intl = useIntl();
  const dispatch = useDispatch();

  const openRemoveList = () => setIsRemove(true);
  const closeRemoveList = () => setIsRemove(false);

  const handleRemoveListing = async (listId) => {
    setIsRemove(false);
    await dispatch(removeListing(listId));
    await refetch({ searchKey });
  };

  const handlePreview = (listId) => {
    history.push('/rooms/' + listId + '/preview');
  };

  const handleEditListing = (listId) => {
    history.push('/become-a-host/' + listId + '/home');
  };

  const handleChange = (event) => {
    const action = event.target.value;
    ManagePublishStatus(propListId, action);
  };

  const percentage = useCallback((data) => {
    let percentage = 0;
    const { step1, step2, step3, step4 } = data.listingSteps || {};
    const listPhotos = data.listPhotos || [];

    if (step1 === "active") percentage += 0.20;
    if (step1 === "completed") percentage += 0.30;
    if (step2 === "completed") percentage += 0.20;
    if (listPhotos.length > 0) percentage += 0.10;
    if (step4 === "completed") percentage += 0.40;

    return Math.round(100 * percentage);
  }, []);

  const title = (data) => {
    if (data?.title) return data.title;
    return data?.settingsData?.[0]?.listsettings?.itemName + " in " + data?.city;
  };

  const removeItem = (listId) => (
    <li className={s.panelBodyAlert}>
      <div className={cx(s.alertBlock)}>
        <div>
          <h3 className={s.heading}><FormattedMessage {...messages.deleteListing} /></h3>
          <p className={s.text}><FormattedMessage {...messages.deleteListingInfo} /></p>
          <div>
            <Button
              className={cx(s.button, bt.btnPrimary, bt.btnlarge, s.spaceRight2, s.smButton, 'spaceRight2AR')}
              onClick={() => handleRemoveListing(listId)}>
              <FormattedMessage {...messages.delete} />
            </Button>
            <Button
              className={cx(s.button, bt.btnPrimaryBorder, bt.btnLarge, s.smButton, s.marginTopMb)}
              onClick={closeRemoveList}>
              <FormattedMessage {...messages.goBack} />
            </Button>
          </div>
        </div>
      </div>
    </li>
  );

  if (!data) return null;

  const listId = data.id;
  const updatedDate = moment(parseInt(data.lastUpdatedAt)).format('Do MMMM YYYY');
  const coverPhoto = data.coverPhoto;
  const listPhotos = data.listPhotos || [];

  if (isRemove) return removeItem(listId);

  return (
    <div className={s.pageBg}>
    <li className={cx(s.panelBody)}>
      <Row>
        <Col xs={12} sm={12} className={cx('hidden-md hidden-lg', s.space2)}>
          <a href="javascript:void(0);" onClick={openRemoveList}>
            <img src={closeIcon} className={cx(s.iconRemove, s.icon, 'pull-right', 'pullLeftHeaderAR')} />
          </a>
        </Col>

        <Col xs={12} sm={12} md={5} lg={4}>
          <div className={s.listPhotoCover}>
            <div className={s.listPhotoMedia}>
              <a target="_blank" href={`/rooms/${listId}/preview`}>
                <ListCoverPhoto
                  className={s.imgResponsive}
                  coverPhoto={coverPhoto}
                  listPhotos={listPhotos}
                  photoType="x_medium"
                />
              </a>
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} md={7} lg={8} className={s.listDetailContent}>
          <a href="javascript:void(0);" onClick={openRemoveList} className="svgImg">
            <img src={closeIcon} className={cx(s.iconRemove, s.icon, "hidden-sm hidden-xs", 'pull-right', 'iconRemoveRtl')} />
          </a>

          <Col xs={12} sm={12} md={10} lg={10} className={cx(s.noPadding, s.textOverFlow)}>
            <a target="_blank" href={`/rooms/${listId}/preview`} className={s.listTitleSection}>
              <span className={cx(s.listContent)}> {title(data)} </span>
            </a>
          </Col>

          <Col xs={12} sm={12} md={10} lg={10} className={cx(s.noPadding, s.spaceTop1)}>
            <span className={cx(s.landingStep)}>
              <FormattedMessage {...messages.listingUpdateInfo} /> {updatedDate}
            </span>
          </Col>

          <Col xs={12} sm={12} md={10} lg={10} className={cx(s.noPadding, s.spaceTop2, s.space2)}>
            <ProgressBar
              bsClass={s.leanProgressBar}
              className={s.leanProgressContainer}
              now={percentage(data)}
            />
            <span className={s.labelText}>
              <FormattedMessage {...messages.progressBarText1} /> {percentage(data)}% <FormattedMessage {...messages.progressBarText2} />
            </span>
          </Col>

          <Button className={cx(s.button, bt.btnPrimary, s.spaceRight2, s.spaceTop2, s.smButton, 'spaceRight2AR')} onClick={() => handleEditListing(listId)}>
            {
              data.listingSteps.step4 === "completed" && listPhotos.length > 0
                ? <FormattedMessage {...messages.editListing} />
                : <FormattedMessage {...messages.finishListing} />
            }
          </Button>

          {data?.isReady && (
            <a
              href={`/rooms/${listId}/preview`}
              target="_blank"
              className={cx('btn btn-default', s.button, bt.btnPrimaryBorder, s.spaceTop2, s.smButton, s.spaceRight2, 'spaceRight2AR', s.noBg)}
            >
              <FormattedMessage {...messages.preview} />
            </a>
          )}

          {data?.isReady && data.user?.userBanStatus !== 1 && data.listApprovalStatus === 'approved' && (
            <PublishOption
              listId={listId}
              isPublished={data.isPublished}
            />
          )}
        </Col>
      </Row>
    </li>
    </div>
  );
};

ListItem.propTypes = {
  data: PropTypes.object,
  refetch: PropTypes.func,
  searchKey: PropTypes.string,
  listId: PropTypes.number,
  ManagePublishStatus: PropTypes.func,
};

export default withStyles(s, bt)(ListItem);